<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { useLotteryStore } from '../stores/lottery.js'
import { useAuditStore } from '../stores/auditStore.js'
import { usePaymentStore } from '../stores/paymentStore.js'
import { useNotificationStore } from '../stores/notificationStore.js'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import { isDataStale } from '../utils/operationsUi.js'
import {
  downloadCsv,
  getRecentExportHistory,
  recordExportEvent,
  toCsv,
  toFailedOperationsRows,
  toLifecycleFilename,
  toNotificationDeliveryRows,
  toPaymentReconciliationRows,
} from '../utils/exportUi.js'

const authStore = useAuthStore()
const orgStore = useOrgStore()
const lotteryStore = useLotteryStore()
const auditStore = useAuditStore()
const paymentStore = usePaymentStore()
const notificationStore = useNotificationStore()

const loading = ref(false)
const error = ref('')
const lastSuccessfulRefreshAt = ref('')
const exportDialogOpen = ref(false)
const exportScope = ref('failed-operations')
const exportLoading = ref(false)
const exportError = ref('')
const exportHistory = ref([])

const activeLotteries = computed(() => (lotteryStore.lotteries || []).filter((item) => String(item?.status || '').toLowerCase() === 'active').length)
const drawnLotteries = computed(() => (lotteryStore.lotteries || []).filter((item) => String(item?.status || '').toLowerCase() === 'drawn').length)
const unresolvedIntegrityWarnings = computed(() => {
  return (lotteryStore.lotteries || []).reduce((count, item) => {
    return count + Number(item?.integrity_warning_count ?? item?.unresolved_warnings ?? 0)
  }, 0)
})
const orphanedAssignments = computed(() => {
  return (lotteryStore.lotteries || []).reduce((count, item) => {
    return count + Number(item?.orphaned_assignments ?? 0)
  }, 0)
})
const failedOrRecoverableOperations = computed(() => {
  return (lotteryStore.lotteries || []).filter((item) => {
    const status = String(item?.execution_status || '').toLowerCase()
    return status === 'failed' || status === 'recoverable' || status === 'partial'
  }).length
})
const failedPayments = computed(() => paymentStore.payments.filter((item) => item.status === 'failed' || item.partial === true || item.degraded === true).length)
const pendingPayments = computed(() => paymentStore.payments.filter((item) => item.status === 'pending' || item.status === 'authorized').length)
const failedNotifications = computed(() => notificationStore.notifications.filter((item) => item.status === 'failed' || item.status === 'bounced').length)
const deliveredNotifications = computed(() => notificationStore.notifications.filter((item) => item.status === 'delivered').length)
const recentFailures = computed(() => {
  return [
    ...paymentStore.payments.filter((item) => item.status === 'failed' || item.partial === true).map((item) => ({
      key: `payment-${item.id}`,
      type: 'payment',
      label: item.reference || `Payment #${item.id}`,
      status: item.status,
      updatedAt: item.updatedAt,
    })),
    ...notificationStore.notifications.filter((item) => item.status === 'failed' || item.status === 'bounced').map((item) => ({
      key: `notification-${item.id}`,
      type: 'notification',
      label: item.subject || `Notification #${item.id}`,
      status: item.status,
      updatedAt: item.updatedAt,
    })),
  ].slice(0, 8)
})

const recentAuditEvents = computed(() => (auditStore.logs || []).slice(0, 5))
const recentDraws = computed(() => {
  return [...(lotteryStore.lotteries || [])]
    .filter((item) => item?.last_executed_at || item?.executed_at)
    .sort((a, b) => Date.parse(b?.last_executed_at || b?.executed_at || '') - Date.parse(a?.last_executed_at || a?.executed_at || ''))
    .slice(0, 5)
})

const staleData = computed(() => isDataStale(lastSuccessfulRefreshAt.value, 120000))

const refresh = async () => {
  loading.value = true
  error.value = ''
  try {
    await Promise.all([
      lotteryStore.fetchLotteries(),
      auditStore.fetchAuditLogs(),
      paymentStore.fetchPaymentOperations(),
      notificationStore.fetchNotifications(),
      notificationStore.fetchHistory(),
    ])
    lastSuccessfulRefreshAt.value = new Date().toISOString()
    exportHistory.value = getRecentExportHistory()
  } catch {
    error.value = 'Failed to refresh operational overview.'
  } finally {
    loading.value = false
  }
}

const openExportDialog = (scope) => {
  exportScope.value = scope
  exportError.value = ''
  exportDialogOpen.value = true
}

const closeExportDialog = () => {
  if (exportLoading.value) return
  exportDialogOpen.value = false
}

const exportRows = computed(() => {
  if (exportScope.value === 'payment-reconciliation') return toPaymentReconciliationRows(paymentStore.payments)
  if (exportScope.value === 'notification-delivery') return toNotificationDeliveryRows(notificationStore.notifications)
  return toFailedOperationsRows({ payments: paymentStore.payments, notifications: notificationStore.notifications })
})

const exportOperationsCsv = async () => {
  if (!exportRows.value.length || exportLoading.value) return
  exportLoading.value = true
  exportError.value = ''
  try {
    const headers = Object.keys(exportRows.value[0] || {}).map((key) => ({ key, label: key }))
    const csv = toCsv(exportRows.value, headers)
    const filename = toLifecycleFilename({ scope: exportScope.value, lifecycle: 'operational' })
    downloadCsv(filename, csv)
    recordExportEvent({
      exported_by: authStore.user?.username || authStore.user?.email || 'unknown',
      export_scope: exportScope.value,
      partial_export_warning: paymentStore.degradedWarning || notificationStore.degradedWarning,
      record_count: exportRows.value.length,
    })
    exportHistory.value = getRecentExportHistory()
    exportDialogOpen.value = false
  } catch {
    exportError.value = 'Failed to export operational report.'
  } finally {
    exportLoading.value = false
  }
}

onMounted(async () => {
  await refresh()
})
</script>

<template>
  <section class="operations-view">
    <header class="header-row">
      <div>
        <h1>Operational Overview</h1>
        <p class="muted">Read-only monitoring summary for operational admins.</p>
        <p v-if="lastSuccessfulRefreshAt" class="muted">Last successful refresh: {{ lastSuccessfulRefreshAt }}</p>
      </div>
      <div class="actions-row">
        <button class="btn" type="button" :disabled="loading" @click="refresh">{{ loading ? 'Refreshing...' : 'Refresh' }}</button>
        <button class="btn" type="button" :disabled="loading || !paymentStore.payments.length" @click="openExportDialog('payment-reconciliation')">Export Payments</button>
        <button class="btn" type="button" :disabled="loading || !notificationStore.notifications.length" @click="openExportDialog('notification-delivery')">Export Notifications</button>
        <button class="btn" type="button" :disabled="loading || !recentFailures.length" @click="openExportDialog('failed-operations')">Export Failures</button>
      </div>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="staleData && !loading" class="warning">Operational overview may be stale. Retry to confirm current status.</p>
    <p v-if="paymentStore.degradedWarning || notificationStore.degradedWarning" class="warning">{{ paymentStore.degradedWarning || notificationStore.degradedWarning }}</p>

    <div class="grid">
      <article class="card"><h2>Organizations</h2><p>{{ orgStore.organizations.length }}</p></article>
      <article class="card"><h2>Active Lotteries</h2><p>{{ activeLotteries }}</p></article>
      <article class="card"><h2>Drawn Lotteries</h2><p>{{ drawnLotteries }}</p></article>
      <article class="card"><h2>Integrity Warnings</h2><p>{{ unresolvedIntegrityWarnings }}</p></article>
      <article class="card"><h2>Orphaned Assignments</h2><p>{{ orphanedAssignments }}</p></article>
      <article class="card"><h2>Failed/Recoverable Ops</h2><p>{{ failedOrRecoverableOperations }}</p></article>
      <article class="card"><h2>Failed Payments</h2><p>{{ failedPayments }}</p></article>
      <article class="card"><h2>Pending Payments</h2><p>{{ pendingPayments }}</p></article>
      <article class="card"><h2>Failed Notifications</h2><p>{{ failedNotifications }}</p></article>
      <article class="card"><h2>Delivered Notifications</h2><p>{{ deliveredNotifications }}</p></article>
    </div>

    <section class="panel">
      <h2>Recent Audit Events</h2>
      <ul v-if="recentAuditEvents.length" class="list">
        <li v-for="event in recentAuditEvents" :key="event.id || `${event.action}-${event.created_at}`">
          {{ event.action || 'UNKNOWN' }} - {{ event.created_at || event.timestamp || 'Timestamp unavailable' }}
        </li>
      </ul>
      <p v-else class="muted">No audit events loaded.</p>
    </section>

    <section class="panel">
      <h2>Recent Draws</h2>
      <ul v-if="recentDraws.length" class="list">
        <li v-for="lottery in recentDraws" :key="lottery.id || lottery.lottery_id">
          {{ lottery.name || `Lottery #${lottery.id || lottery.lottery_id}` }} - {{ lottery.last_executed_at || lottery.executed_at }}
        </li>
      </ul>
      <p v-else class="muted">No draws recorded yet.</p>
    </section>

    <section class="panel">
      <h2>Recent Failures</h2>
      <ul v-if="recentFailures.length" class="list">
        <li v-for="entry in recentFailures" :key="entry.key">
          {{ entry.type }} - {{ entry.label }} - {{ entry.status }} - {{ entry.updatedAt || 'Timestamp unavailable' }}
        </li>
      </ul>
      <p v-else class="muted">No recent payment or notification failures.</p>
    </section>

    <section class="panel">
      <h2>Recent Exports</h2>
      <ul v-if="exportHistory.length" class="list">
        <li v-for="entry in exportHistory" :key="entry.id">
          {{ entry.export_scope }} - {{ entry.record_count }} rows - {{ entry.exported_at }}
          <span v-if="entry.partial_export_warning">(partial warning)</span>
        </li>
      </ul>
      <p v-else class="muted">No operational exports recorded yet.</p>
    </section>

    <AdminActionDialog
      :open="exportDialogOpen"
      title="Export Operational Report"
      :message="`Export scope: ${exportScope}.`"
      :details="[
        exportRows.length ? `Records: ${exportRows.length}` : 'No records available.',
        'Exports are tracked for operational support.',
      ]"
      tone="warning"
      confirm-label="Export CSV"
      :loading="exportLoading"
      loading-label="Preparing export..."
      @close="closeExportDialog"
      @confirm="exportOperationsCsv"
    />

    <p v-if="exportError" class="error">{{ exportError }}</p>
  </section>
</template>

<style scoped>
.operations-view { max-width: 1200px; margin: 0 auto; padding: 2rem; display: grid; gap: 1rem; }
.header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
.actions-row { display: flex; flex-wrap: wrap; gap: 0.5rem; justify-content: flex-end; }
.grid { display: grid; gap: 0.75rem; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
.card { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.75rem; }
.panel { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.75rem; }
.list { margin: 0; padding-left: 1rem; color: #374151; }
.muted { color: #6b7280; }
.warning { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 0.45rem; }
.error { color: #991b1b; background: #fee2e2; border-radius: 6px; padding: 0.45rem; }
.btn { border: none; border-radius: 6px; background: #2563eb; color: #fff; padding: 0.45rem 0.75rem; }
</style>

